/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect } from 'react';

import { useDeferredRender } from '@/hooks/use-deferred-render';

import { injectAttentiveTag } from './attentive.utils';

interface AttentiveProps {
    /** Loader URL. First-party Enhanced-Tag host once DNS is live, `cdn.attn.tv` until then. */
    src: string;
    /** Loader URL used if `src` fails to load. Ignored when identical to `src`. */
    fallbackSrc?: string;
}

/**
 * Attentive tag (UCR-2680) — loads the SMS acquisition popup infrastructure.
 *
 * Renders nothing. Mounted once in `root.tsx` (gated on config), so the tag is present on every
 * page. Injection is deferred to an idle frame so the third-party fetch stays off the hydration
 * critical path, matching `<SierraChat>`.
 *
 * **Consent model:** the tag loads unconditionally and this component applies no consent gate of
 * its own. Cookie consent on Black Magic is owned by OneTrust (UCR-2978), so Attentive's tracking
 * layer will be driven from there — `attentive.analytics.enable()` / `.disable()` plus cookie
 * cleanup — exactly as SFRA's OneTrust wrapper does today. The helpers for that
 * (`setAttentiveAnalyticsConsent`, `clearAttentiveCookies` in `./attentive.utils`) are retained
 * for the bridge and are intentionally unused here.
 *
 * Until that bridge lands, Attentive's own cookies are ungated. That is a known, accepted gap —
 * see docs/README-CONSENT-TRACKING.md.
 *
 * **Route changes need no help from us.** The loader installs its own path-change poller, which
 * tears down `#attentive_overlay` and re-invokes `window.__attentive.show()` when
 * `location.pathname` changes — so sign-up units fire across client-side navigation without a
 * page-view tracker of our own.
 */
export default function Attentive({ src, fallbackSrc }: AttentiveProps) {
    const shouldLoad = useDeferredRender();

    useEffect(() => {
        if (!shouldLoad) {
            return;
        }
        void injectAttentiveTag(src, fallbackSrc);
    }, [shouldLoad, src, fallbackSrc]);

    return null;
}
